import React, { createContext, useContext, useReducer, useState } from 'react'
import { useSavingsToolkitApi } from '../api/savings-toolkit-api'
import { StkBoosterTypes } from '../components/Savings/SavingsToolkit/Boosters/StkBoosters'
import {
  BoosterUpdate,
  StkBooster,
  StkToolkitAccount,
  StkBoosterActivity,
  BoosterForBuckets,
} from '../types'
import { API_STATUS } from '../utils/constants'
import { optimisticallyUpdate } from '../utils/syncToolkitAccounts'
import { stkServerErrors } from '../utils/tagging/stkTagging'

type IBoosterType = 'FAS' | 'RUP' | 'SIFI'

type BoostersForBuckets = Record<string, BoosterForBuckets[]>
// Available to context consumers
export interface StkBoostersProps {
  boosters: StkBooster[]
  // TODO - add computed properties for each booster that
  // includes filtered boosters and "saved so far"
  toolkitAccounts: {
    [key in IBoosterType]: StkToolkitAccount[]
  }
  activity: Record<string, StkBoosterActivity>
  boostersForBuckets: Record<string, BoosterForBuckets[]>
  isBoosterUpdating: boolean
  isBoostersLoading: boolean
  hasBoostersError: boolean
  isToolkitAccountsLoading: boolean
  hasToolkitAccountsError: boolean
  isActivityLoading: boolean
  hasActivityError: boolean
  createBooster: (booster: BoosterUpdate) => Promise<void>
  deleteBooster: (accountBoosterId: string) => Promise<void>
  getBoosters: () => Promise<StkBooster[] | undefined>
  getBoosterActivity: (accountBoosterId: string) => Promise<void>
  hydrateToolkitAccountsForType: (boosterType: IBoosterType) => Promise<boolean>
  getToolkitAccountsForType: (boosterType: IBoosterType) => Promise<any>
  updateBooster: (
    booster: Partial<StkBooster> & Record<'boosterId', string>,
  ) => Promise<void>
  refreshBoostersForDeletedBucket: (accountBucketId: string) => void
}

interface IBoostersState {
  boosters: {
    data: StkBooster[]
    status: API_STATUS
  }
  toolkitAccounts: {
    data: {
      [key in IBoosterType]: StkToolkitAccount[]
    }
    status: API_STATUS
  }
  activity: {
    status: API_STATUS
    data: Record<string, StkBoosterActivity>
  }
  boostersForBuckets: {
    data: Record<string, BoosterForBuckets[]>
  }
}

type IReducerAction =
  | {
      type: 'SET_BOOSTERS'
      boosters: StkBooster[]
    }
  | {
      type: 'SET_TOOLKIT_ACCOUNTS'
      toolkitAccounts: StkToolkitAccount[]
      boosterType: IBoosterType
    }
  | {
      type: 'SET_BOOSTER_STATUS'
      status: API_STATUS
    }
  | {
      type: 'SET_TOOLKIT_ACCOUNTS_STATUS'
      status: API_STATUS
    }
  | {
      type: 'SET_ACTIVITY'
      activity: StkBoosterActivity
      accountBoosterId: string
    }
  | { type: 'SET_ACTIVITY_STATUS'; status: API_STATUS }
  | {
      type: 'SET_BOOSTERS_FOR_BUCKETS'
      boostersForBuckets: Record<string, BoosterForBuckets[]>
    }

export const ERRORS_TO_RETRY = ['USB_COM_503', 'USB_BUS_680', 'USB_BUS_702']

export const StkBoostersContext = createContext({} as StkBoostersProps)

export const boostersReducer = (
  state: IBoostersState,
  action: IReducerAction,
): IBoostersState => {
  switch (action.type) {
    case 'SET_BOOSTER_STATUS':
      return {
        ...state,
        boosters: { ...state.boosters, status: action.status },
      }
    case 'SET_TOOLKIT_ACCOUNTS_STATUS':
      return {
        ...state,
        toolkitAccounts: { ...state.toolkitAccounts, status: action.status },
      }
    case 'SET_BOOSTERS':
      return {
        ...state,
        boosters: { ...state.boosters, data: action.boosters },
      }
    case 'SET_TOOLKIT_ACCOUNTS':
      return {
        ...state,
        toolkitAccounts: {
          ...state.toolkitAccounts,
          data: {
            ...state.toolkitAccounts.data,
            [action.boosterType]: action.toolkitAccounts,
          },
        },
      }
    case 'SET_ACTIVITY_STATUS':
      return {
        ...state,
        activity: { ...state.activity, status: action.status },
      }
    case 'SET_ACTIVITY':
      return {
        ...state,
        activity: {
          ...state.activity,
          data: {
            ...state.activity.data,
            [action.accountBoosterId]: action.activity,
          },
        },
      }
    case 'SET_BOOSTERS_FOR_BUCKETS':
      return {
        ...state,
        boostersForBuckets: {
          ...state.boostersForBuckets,
          data: action.boostersForBuckets,
        },
      }
    default: {
      return state
    }
  }
}
// this is a helper function for setBoostersForBuckets that will check if the bucketId exists already and will push a new
// element to the array of boosters if it does, or it creates a new key initializing the array if it doesn't
const newBucketInBoostersForBuckets = (
  bucketId: string,
  acc: BoostersForBuckets,
  curr: StkBooster,
): void => {
  if (bucketId in acc) {
    acc[bucketId].push({
      accountBoosterId: curr.accountBoosterId,
      boosterType: curr.boosterType,
    })
  } else {
    acc[bucketId] = [
      {
        accountBoosterId: curr.accountBoosterId,
        boosterType: curr.boosterType,
      },
    ]
  }
}

// we create an object iterating through the different boosters and adding keys for each bucket
// to have a collection in which each bucketId will have an array with boosters associated to it
export const setBoostersForBuckets = (
  boosters: StkBooster[],
): BoostersForBuckets =>
  boosters.length
    ? boosters.reduce((acc: BoostersForBuckets, curr) => {
        // Recurring Transfers require a special treatment since we need to iterate over the buckets inside the booster
        if (curr.boosterType === 'SIFI') {
          curr.buckets.forEach(bucket => {
            const { bucketId, allocationAmount } = bucket
            // istanbul ignore else // no need to test else
            if (bucketId && allocationAmount && allocationAmount > 0) {
              newBucketInBoostersForBuckets(bucketId, acc, curr)
            }
          })
        }
        // For Surprise Savings and Roundups we can just take the first element of the buckets array since they only have one bucket associated
        else {
          const { bucketId } = curr.buckets[0]
          // istanbul ignore else // no need to test else
          if (bucketId) {
            newBucketInBoostersForBuckets(bucketId, acc, curr)
          }
        }
        return { ...acc }
      }, {})
    : {}

const isErrorToRetry = (err: string): boolean => {
  return ERRORS_TO_RETRY.includes(err)
}

export const StkBoostersProvider: React.FC = ({ children }) => {
  const api = useSavingsToolkitApi()
  const [initialBoosterCallToFixError, setInitialBoosterCallToFixError] =
    useState(false)

  const [state, dispatch] = useReducer(boostersReducer, {
    boosters: {
      status: API_STATUS.IDLE,
      data: [],
    },
    toolkitAccounts: {
      status: API_STATUS.IDLE,
      data: {} as IBoostersState['toolkitAccounts']['data'],
    },
    activity: { status: API_STATUS.IDLE, data: {} },
    boostersForBuckets: { data: {} },
  })

  // Fetch toolkit account data in the background
  // istanbul ignore next // TODO - test
  const refreshToolkitAccountsForType = async (
    boosterType: StkBoosterTypes,
  ): Promise<void> => {
    try {
      const toolkitAccounts = await api.getStkToolkitAccounts(boosterType)
      dispatch({ type: 'SET_TOOLKIT_ACCOUNTS', toolkitAccounts, boosterType })
    } catch (e) {
      console.error(e)
    }
  }

  // Sets the provider state to the provided updated toolkit accounts
  // (optimistically updated) and then kicks off the background request to
  // fetch new toolkit accounts data
  // istanbul ignore next // TODO - test
  const syncToolkitAccounts = (
    updatedAccounts: StkToolkitAccount[],
    boosterType: StkBoosterTypes,
  ): void => {
    dispatch({
      type: 'SET_TOOLKIT_ACCOUNTS',
      boosterType,
      toolkitAccounts: updatedAccounts,
    })
    refreshToolkitAccountsForType(boosterType)
  }

  const createBooster = async (booster: BoosterUpdate): Promise<void> => {
    dispatch({
      type: 'SET_BOOSTER_STATUS',
      status: API_STATUS.UPDATING,
    })
    try {
      const newBoosters = await api.createStkBooster(booster)
      dispatch({ type: 'SET_BOOSTERS', boosters: newBoosters })
      dispatch({
        type: 'SET_BOOSTERS_FOR_BUCKETS',
        boostersForBuckets: setBoostersForBuckets(newBoosters),
      })
      syncToolkitAccounts(
        optimisticallyUpdate(
          state.toolkitAccounts.data[booster.boosterType],
        ).forCreatedBooster(booster),
        booster.boosterType,
      )
      dispatch({
        type: 'SET_BOOSTER_STATUS',
        status: API_STATUS.RESOLVED,
      })
    } catch (e) {
      stkServerErrors.createBoosterServerError(e as Error, booster.boosterType)
      dispatch({ type: 'SET_BOOSTER_STATUS', status: API_STATUS.RESOLVED })
      throw e
    }
  }

  // istanbul ignore next // TODO - test
  const deleteBooster = async (accountBoosterId: string): Promise<void> => {
    const boosterToDelete = state.boosters.data.find(
      b => b.accountBoosterId === accountBoosterId,
    )
    const newBoosters = await api.deleteStkBooster(accountBoosterId)
    dispatch({ type: 'SET_BOOSTERS', boosters: newBoosters })
    dispatch({
      type: 'SET_BOOSTERS_FOR_BUCKETS',
      boostersForBuckets: setBoostersForBuckets(newBoosters),
    })
    if (boosterToDelete) {
      syncToolkitAccounts(
        optimisticallyUpdate(
          state.toolkitAccounts.data[boosterToDelete?.boosterType],
        ).forDeletedBooster(boosterToDelete),
        boosterToDelete.boosterType,
      )
    }
  }

  const getBoosters = async (): Promise<StkBooster[] | undefined> => {
    dispatch({ type: 'SET_BOOSTER_STATUS', status: API_STATUS.PENDING })
    try {
      const boosters = await api.getStkBoosters()
      dispatch({ type: 'SET_BOOSTERS', boosters })
      dispatch({
        type: 'SET_BOOSTERS_FOR_BUCKETS',
        boostersForBuckets: setBoostersForBuckets(boosters),
      })
      dispatch({ type: 'SET_BOOSTER_STATUS', status: API_STATUS.RESOLVED })
      return boosters
    } catch (e: any) {
      // we want to make the booster call one more time for certain errors
      if (isErrorToRetry(e.message) && !initialBoosterCallToFixError) {
        // giving it some time for the user to be inserted in the data base, and then we refresh boosters
        setInitialBoosterCallToFixError(true)
        const boosters = await getBoosters()
        return boosters
      }
      // if we get one of these errors more than once maybe the timeOut was not enough, we fire a custom analytics error so we can check if users are having this issue often
      /* istanbul ignore next */
      if (isErrorToRetry(e.message)) {
        stkServerErrors.stkEnrollmentTimeout(e)
      }
      console.error(e)
      dispatch({ type: 'SET_BOOSTER_STATUS', status: API_STATUS.REJECTED })
      return undefined
    }
  }

  const getBoosterActivity = async (
    accountBoosterId: string,
  ): Promise<void> => {
    dispatch({ type: 'SET_ACTIVITY_STATUS', status: API_STATUS.PENDING })
    try {
      const activity = await api.getStkBoosterActivity(accountBoosterId)
      dispatch({ type: 'SET_ACTIVITY', activity, accountBoosterId })
      dispatch({ type: 'SET_ACTIVITY_STATUS', status: API_STATUS.RESOLVED })
    } catch (e) {
      /* istanbul ignore next */
      stkServerErrors.getBoosterTransactionsServerError(e as Error)
      /* istanbul ignore next */
      console.error(e)
      /* istanbul ignore next */
      dispatch({ type: 'SET_ACTIVITY_STATUS', status: API_STATUS.REJECTED })
    }
  }

  // Returns a boolean indicating successful toolkit account fetch
  // to be used by the UI in determining whether to proceed navigation or not
  const hydrateToolkitAccountsForType = async (
    boosterType: IBoosterType,
  ): Promise<boolean> => {
    dispatch({
      type: 'SET_TOOLKIT_ACCOUNTS_STATUS',
      status: API_STATUS.PENDING,
    })
    try {
      const toolkitAccounts = await api.getStkToolkitAccounts(boosterType)
      dispatch({ type: 'SET_TOOLKIT_ACCOUNTS', toolkitAccounts, boosterType })
      dispatch({
        type: 'SET_TOOLKIT_ACCOUNTS_STATUS',
        status: API_STATUS.RESOLVED,
      })
      return true
    } catch (e) {
      console.error(e)
      dispatch({
        type: 'SET_TOOLKIT_ACCOUNTS_STATUS',
        status: API_STATUS.REJECTED,
      })
      return false
    }
  }

  // returns toolkit accounts
  const getToolkitAccountsForType = async (
    boosterType: IBoosterType,
  ): Promise<any> => {
    dispatch({
      type: 'SET_TOOLKIT_ACCOUNTS_STATUS',
      status: API_STATUS.PENDING,
    })
    try {
      const toolkitAccounts = await api.getStkToolkitAccounts(boosterType)
      dispatch({ type: 'SET_TOOLKIT_ACCOUNTS', toolkitAccounts, boosterType })
      dispatch({
        type: 'SET_TOOLKIT_ACCOUNTS_STATUS',
        status: API_STATUS.RESOLVED,
      })
      return toolkitAccounts
    } catch (e) {
      console.error(e)
      dispatch({
        type: 'SET_TOOLKIT_ACCOUNTS_STATUS',
        status: API_STATUS.REJECTED,
      })
      return []
    }
  }

  // istanbul ignore next // TODO - test
  const updateBooster = async (
    booster: Partial<StkBooster> & Record<'boosterId', string>,
  ): Promise<void> => {
    dispatch({
      type: 'SET_BOOSTER_STATUS',
      status: API_STATUS.UPDATING,
    })
    try {
      const originalBooster = state.boosters.data.find(
        b => b.accountBoosterId === booster.boosterId,
      )
      const boosters = await api.updateStkBooster(booster)
      if (boosters) {
        dispatch({ type: 'SET_BOOSTERS', boosters })
        dispatch({
          type: 'SET_BOOSTERS_FOR_BUCKETS',
          boostersForBuckets: setBoostersForBuckets(boosters),
        })
        if (originalBooster) {
          syncToolkitAccounts(
            optimisticallyUpdate(
              state.toolkitAccounts.data[originalBooster.boosterType],
            ).forUpdatedBooster(originalBooster, booster),
            originalBooster.boosterType as StkBoosterTypes,
          )
        }
      }
      dispatch({
        type: 'SET_BOOSTER_STATUS',
        status: API_STATUS.RESOLVED,
      })
    } catch (e) {
      dispatch({ type: 'SET_BOOSTER_STATUS', status: API_STATUS.RESOLVED })
      stkServerErrors.updateBoosterServerError(e as Error, booster.boosterType)
      throw e
    }
  }

  // Fetch booster data in the background (no loading state)
  // used to refresh data after the initial loading
  // istanbul ignore next // TODO - test
  const refreshBoosters = async (): Promise<void> => {
    try {
      const boosters = await api.getStkBoosters()
      dispatch({ type: 'SET_BOOSTERS', boosters })
    } catch (e) {
      stkServerErrors.getBoostersServerError(e as Error)
      dispatch({ type: 'SET_BOOSTER_STATUS', status: API_STATUS.REJECTED })
    }
  }

  // Called by the buckets provider every time a bucket is deleted
  // to refresh boosters only when that bucket had a booster associated.
  // istanbul ignore next // TODO - test
  const refreshBoostersForDeletedBucket = (accountBucketId: string): void => {
    const deletedBucketHasBooster = state.boosters.data.some(booster =>
      booster.buckets.some(bucket => bucket.bucketId === accountBucketId),
    )
    if (deletedBucketHasBooster) {
      refreshBoosters()
    }
  }

  return (
    <StkBoostersContext.Provider
      value={{
        // General data
        boosters: state.boosters.data,
        toolkitAccounts: state.toolkitAccounts.data,
        activity: state.activity.data,
        boostersForBuckets: state.boostersForBuckets.data,
        // Error/loading states
        isBoosterUpdating: state.boosters.status === API_STATUS.UPDATING,
        isBoostersLoading: state.boosters.status === API_STATUS.PENDING,
        hasBoostersError: state.boosters.status === API_STATUS.REJECTED,
        isToolkitAccountsLoading:
          state.toolkitAccounts.status === API_STATUS.PENDING,
        hasToolkitAccountsError:
          state.toolkitAccounts.status === API_STATUS.REJECTED,
        isActivityLoading: state.activity.status === API_STATUS.PENDING,
        hasActivityError: state.activity.status === API_STATUS.REJECTED,

        // Fetching functions
        getBoosters,
        getBoosterActivity,
        hydrateToolkitAccountsForType,
        getToolkitAccountsForType,

        // Create, Update, Delete functions
        createBooster,
        updateBooster,
        deleteBooster,

        // other
        refreshBoostersForDeletedBucket,
      }}
    >
      {children}
    </StkBoostersContext.Provider>
  )
}

export const useStkBoosters = (): StkBoostersProps =>
  useContext(StkBoostersContext)
